export interface ActivityLogPropertyChange {
  PropertyName?: string;
  propertyName?: string;
  OldValue?: string;
  oldValue?: string;
  NewValue?: string;
  newValue?: string;
}

export function getActivityLogPropertyName(change: ActivityLogPropertyChange): string | undefined {
  return change?.PropertyName ?? change?.propertyName;
}

export function getActivityLogOldValue(change: ActivityLogPropertyChange): string | undefined {
  return change?.OldValue ?? change?.oldValue;
}

const DISPLAY_NAME_ALIASES: Record<string, string[]> = {
  Tags: ['OfferTag'],
  Categories: ['ProductCategory'],
  Badge: ['Asset'],
  PageNumber: ['PrintPage', 'PageFrom'],
};

/**
 * Expands form/display property keys so they match activity-log property names.
 * Edge changes use sink vertex labels (e.g. OfferType, Asset); vertex diffs skip *Id properties.
 */
export function expandPropertyKeysForModificationCheck(
  propertyKeys: string[]
): string[] {
  const expanded = new Set<string>();

  for (const key of propertyKeys) {
    if (!key) {
      continue;
    }

    expanded.add(key);

    const displayAliases = DISPLAY_NAME_ALIASES[key];
    if (displayAliases) {
      displayAliases.forEach((alias) => expanded.add(alias));
    }

    if (key.endsWith('Ids')) {
      expanded.add(key.slice(0, -3));
    } else if (key.endsWith('Id')) {
      const base = key.slice(0, -2);
      expanded.add(base);
      expanded.add(`${base}Name`);

      if (key.endsWith('AssetId')) {
        expanded.add('Asset');
      }
    }
  }

  return Array.from(expanded);
}

export function isPropertyModifiedInList(
  modifiedPropertyNames: string[],
  showChanges: boolean,
  ...propertyKeys: string[]
): boolean {
  if (!showChanges || !modifiedPropertyNames?.length) {
    return false;
  }

  const normalizedModified = new Set(
    modifiedPropertyNames
      .map((name) => name?.toLowerCase())
      .filter((name): name is string => !!name)
  );

  const keysToCheck = expandPropertyKeysForModificationCheck(propertyKeys);

  return keysToCheck.some((key) => normalizedModified.has(key.toLowerCase()));
}

/** Strip HTML tags and collapse whitespace from activity-log value strings. */
export function stripHtmlToPlainText(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Returns OldValue as plain text for the first matching property key (same alias rules as highlight). */
export function getPreviousStepOldValue(
  oldValuesByProperty: Record<string, string>,
  showChanges: boolean,
  ...propertyKeys: string[]
): string | null {
  if (!showChanges || !oldValuesByProperty || !propertyKeys?.length) {
    return null;
  }

  const lookup = new Map(
    Object.entries(oldValuesByProperty).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  );

  for (const key of expandPropertyKeysForModificationCheck(propertyKeys)) {
    const plain = stripHtmlToPlainText(lookup.get(key.toLowerCase()));
    if (plain) {
      return plain;
    }
  }

  return null;
}
